import type { LyricCue, LyricDisplayMode } from '../types';

const LRC_LINE = /\[(\d{1,2}):(\d{2})(?:\.(\d{1,3}))?\](.*)/;

function parseTimestamp(min: string, sec: string, frac?: string): number {
  const ms = frac ? Number(frac.padEnd(3, '0').slice(0, 3)) : 0;
  return Number(min) * 60_000 + Number(sec) * 1000 + ms;
}

export function normalizeLyricText(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s']/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function parseLrc(lrc: string): LyricCue[] {
  const lines = lrc.split('\n').map((l) => l.trim()).filter(Boolean);
  const raw: { startMs: number; text: string }[] = [];

  for (const line of lines) {
    const match = line.match(LRC_LINE);
    if (!match) continue;
    const text = match[4].trim();
    if (!text || text.startsWith('[')) continue;
    raw.push({
      startMs: parseTimestamp(match[1], match[2], match[3]),
      text,
    });
  }

  raw.sort((a, b) => a.startMs - b.startMs);

  return raw.map((cue, i) => {
    const endMs = raw[i + 1]?.startMs ?? cue.startMs + 4000;
    const normalizedText = normalizeLyricText(cue.text);
    return {
      startMs: cue.startMs,
      endMs,
      text: cue.text,
      normalizedText,
      weight: 1,
      isRepeated: false,
      isLikelyHook: false,
    };
  });
}

export interface LrcSearchResult {
  id: number;
  trackName: string;
  artistName: string;
  syncedLyrics?: string;
  plainLyrics?: string;
  duration?: number;
}

export async function fetchSyncedLyrics(
  title: string,
  artist: string,
  durationMs: number
): Promise<LyricCue[]> {
  const params = new URLSearchParams({
    track_name: title,
    artist_name: artist,
  });
  if (durationMs > 0) params.set('duration', String(Math.round(durationMs / 1000)));

  try {
    const res = await fetch(`https://lrclib.net/api/search?${params}`);
    if (!res.ok) return [];
    const results = (await res.json()) as LrcSearchResult[];
    if (!Array.isArray(results) || results.length === 0) return [];

    const best = results.find((r) => r.syncedLyrics) ?? results[0];
    if (best?.syncedLyrics) return parseLrc(best.syncedLyrics);

    if (best?.plainLyrics) {
      const lines = best.plainLyrics.split('\n').filter((l) => l.trim());
      const slice = Math.max(1, Math.floor(durationMs / lines.length));
      return lines.map((text, i) => ({
        startMs: i * slice,
        endMs: (i + 1) * slice,
        text: text.trim(),
        normalizedText: normalizeLyricText(text),
        weight: 1,
        isRepeated: false,
        isLikelyHook: false,
      }));
    }
  } catch {
    return [];
  }
  return [];
}

export function displayLyricFragment(text: string): string {
  const cleaned = text.replace(/\([^)]*\)/g, '').trim();
  const words = cleaned.split(/\s+/).filter(Boolean);
  if (words.length <= 5) return cleaned.toUpperCase();
  return words.slice(0, 5).join(' ').toUpperCase();
}

export function activeLyricAt(lyrics: LyricCue[], progressMs: number): LyricCue | null {
  return lyrics.find((l) => progressMs >= l.startMs && progressMs < l.endMs) ?? null;
}

export function bestHookAt(lyrics: LyricCue[], progressMs: number): LyricCue | null {
  const hooks = lyrics.filter((l) => l.isLikelyHook);
  if (hooks.length === 0) return activeLyricAt(lyrics, progressMs);
  return hooks.find((l) => progressMs >= l.startMs && progressMs < l.endMs)
    ?? hooks.reduce((best, l) => (l.weight > best.weight ? l : best), hooks[0]);
}

export function lyricForDisplay(
  lyrics: LyricCue[],
  hooks: LyricCue[],
  progressMs: number,
  mode: LyricDisplayMode,
  forceLyricMode: boolean
): string {
  if (mode === 'off') return '';

  const active = activeLyricAt(lyrics, progressMs);
  const fallbackHook = hooks[0] ?? lyrics.find((cue) => cue.isLikelyHook) ?? lyrics[0];
  const forced = forceLyricMode ? fallbackHook : null;
  const cue = mode === 'hooks'
    ? bestHookAt(lyrics, progressMs) ?? forced
    : active ?? forced;

  if (!cue) return '';
  const cleaned = cue.text.replace(/\([^)]*\)/g, '').trim();
  return mode === 'full' ? cleaned.toUpperCase() : displayLyricFragment(cue.text);
}
